import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import OfficeService from '../../../Services/OfficeService';

// Trivial placeholder: the wizard only instantiates windowDescription.itemType when no
// editedItem prop is passed. OfficeAdmin always passes the selected office (crudType=Update),
// so this is never invoked, but it keeps the windowDescription contract satisfied.
class OfficeGeneralEditItem {}

export default class EditOfficeGeneralWindowDescription {
  static titleForEdit = (item) => `Edit General Information (${item.Name})`;
  static itemType = OfficeGeneralEditItem;
  static finishButtonLabel = 'Save Office';
  static messageOnSuccess = 'Office has been saved.';

  // Mirrors the old panel's clickOnSubmitButton: the whole edited office object is posted back
  // (OfficeService.EditOffice string-converts and PUTs the entire office). MUST return the
  // promise (wizard contract). The office list cache is reloaded afterwards, because the office's name and office
  // group feed every office picker.
  static serviceFunction = (edited) =>
    OfficeService.EditOffice(edited).then((result) => {
      OfficeService.ReloadOfficesForSelect().catch(() => {});
      return result;
    });

  static pagesInGeneralEditWizzard = [
    {
      label: 'General',
      fields: [
        {
          fieldName: 'ManagingDirector',
          label: 'Office Managing Director',
          className: 'col-8',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'Name',
          label: 'Office Name',
          className: 'col-4',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'BillingCurId',
          label: 'Office Deffault Currency',
          className: 'col-4',
          type: InputTypesEnum.CurrencySelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'SalesContact',
          label: 'Sales Contact',
          className: 'col-8',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'ShowFulfillmentDate',
          label: 'Show Fulfillment Date?',
          className: 'col-4',
          type: InputTypesEnum.Bool,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'OperationsContact',
          label: 'Operations Contact',
          className: 'col-8',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'DefaultPaymentMethod',
          label: 'Default Payment Method',
          className: 'col-4',
          type: InputTypesEnum.Select,
          selectvalues: EditOfficeGeneralWindowDescription.getPaymentMethodValues(),
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'AccountingFinanceContact',
          label: 'Accounting/Finance Contact',
          className: 'col-8',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'MaxDirectDepositAmount',
          label: 'Max Direct Deposit Amount',
          className: 'col-4',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: ValidityHelper.forRequiredNumberFieldGreaterThanZero,
        },
        {
          fieldName: 'EvaluatorContact',
          label: 'Evaluator Contact',
          className: 'col-8',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'TaxNumber',
          label: 'Tax Number',
          className: 'col-4',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'UserNameSuffix',
          label: 'User Name Suffix',
          className: 'col-4',
          type: InputTypesEnum.Text,
          validityChecker: (e) => ValidityHelper.forMaximizedLengthField(e, 10),
        },
        {
          fieldName: 'CreditMemoApprover',
          label: 'Credit Memo Approver',
          className: 'col-8',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'TimeZoneOffset',
          label: 'TimeZoneOffset to UTC',
          className: 'col-4',
          type: InputTypesEnum.Number,
          validityChecker: ValidityHelper.forRequiredNumberField,
        },
        {
          fieldName: 'ResourceManagementManager',
          label: 'Resource Management Manager',
          className: 'col-4',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'HumanResourcesManager',
          label: 'Human Resources Manager',
          className: 'col-4',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'DataQualityManager',
          label: 'Data Quality Manager',
          className: 'col-4',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'EmployeeTaxPrc',
          label: 'Employee Tax Prc',
          className: 'col-4',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: ValidityHelper.forRequiredNumberFieldGreaterThanZero,
        },
        {
          fieldName: 'MissedInvoiceNotificationGroupId',
          label: 'Missed Invoice Notification Group',
          className: 'col-4',
          type: InputTypesEnum.ActiveGroupSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'HighValueReportGroupId',
          label: 'High Value Report Group',
          className: 'col-4',
          type: InputTypesEnum.ActiveGroupSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'UninvoicedShopsReportGroupId',
          label: 'Uninvoiced Shops Report Group',
          className: 'col-4',
          type: InputTypesEnum.ActiveGroupSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'OfficeGroupId',
          label: 'Office Group',
          className: 'col-4',
          type: InputTypesEnum.OfficeDetailsGroupSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
          tooltipText:
            "A shared group the office belongs to, or the office's own name to keep it in a group of its own. Offices in the same group are treated as one office when filtering by office.",
        },
        {
          fieldName: 'PaymentOfficeIds',
          label: 'Alternate Vendor Payment Offices',
          className: 'col-4',
          type: InputTypesEnum.OfficeMultiSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
          tooltipText: "The Office's alternate offices for paying vendors.",
        },
        {
          fieldName: 'BankInformation',
          label: 'Bank Information',
          className: 'col-12',
          type: InputTypesEnum.TextBox,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
      ],
    },
  ];

  static getPaymentMethodValues() {
    return [
      { label: 'Direct Deposit', value: 'D', disabled: false },
      { label: 'Check', value: 'C', disabled: false },
      { label: 'International', value: 'I', disabled: false },
      { label: 'Tipalti', value: 'T', disabled: false },
    ];
  }
}
